import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { JournalEntry, JournalDetail } from "./models";
import { LabState, LabCommand, LabRun } from "../../api/apiServices/backOffice/controllers/quoteAutofillLab/quoteAutofillLab.requestsDto";
export type Filters = {
  page: number;
  size: number;
  applicationId?: number;
  userId?: number;
  status?: string;
  range?: [dayjs.Dayjs, dayjs.Dayjs];
};
const initialState: {
  filters: Filters;
  items: JournalEntry[];
  total: number;
  loading: boolean;
  failed: boolean;
  selectedId?: number;
  detail?: JournalDetail;
  detailLoading: boolean;
  detailFailed: boolean;
  revision: number;
  lab?: LabState;
  caseSource?: { name: string; sourceText: string };
  labLoading: boolean;
  labError: boolean;
  busy: boolean;
  models: string[];
  output?: { operation: string; value: unknown };
  batch: { running: boolean; stop: boolean; total: number; done: number; results: LabRun[] };
} = {
  filters: { page: 0, size: 25 },
  items: [],
  total: 0,
  loading: false,
  failed: false,
  detailLoading: false,
  detailFailed: false,
  revision: 0,
  labLoading: false,
  labError: false,
  busy: false,
  models: [],
  batch: { running: false, stop: false, total: 0, done: 0, results: [] },
};
export default createSlice({
  name: "quoteAutofillLab",
  initialState,
  reducers: {
    createCaseFromJournal(state, action: PayloadAction<{ name: string; sourceText: string }>) {
      state.caseSource = action.payload;
      state.selectedId = undefined;
      state.detail = undefined;
    },
    loadLab(state) {
      state.labLoading = true;
      state.labError = false;
    },
    loadLabSuccess(state, action: PayloadAction<LabState>) {
      state.lab = action.payload;
      state.labLoading = false;
    },
    loadLabReject(state) {
      state.labLoading = false;
      state.labError = true;
    },
    loadModels(state) {
      state.busy = true;
      state.labError = false;
    },
    loadModelsSuccess(state, action: PayloadAction<string[]>) {
      state.models = action.payload;
      state.busy = false;
    },
    loadModelsReject(state) {
      state.busy = false;
      state.labError = true;
    },
    command(state, _action: PayloadAction<LabCommand>) {
      state.busy = true;
      state.output = undefined;
      state.labError = false;
    },
    commandSuccess(state, action: PayloadAction<{ operation: string; value: unknown }>) {
      state.busy = false;
      state.output = action.payload;
    },
    commandReject(state) {
      state.busy = false;
      state.labError = true;
    },
    exportVersion(state, _action: PayloadAction<string>) {
      state.busy = true;
      state.labError = false;
    },
    exportVersionSuccess(state) {
      state.busy = false;
    },
    exportVersionReject(state) {
      state.busy = false;
      state.labError = true;
    },
    startBatch(state, action: PayloadAction<{ cases: { id: string; revision: number }[]; versions: (string | null)[]; repeats: number }>) {
      state.batch = {
        running: true,
        stop: false,
        total: action.payload.cases.length * action.payload.versions.length * action.payload.repeats,
        done: 0,
        results: [],
      };
      state.labError = false;
    },
    stopBatch(state) {
      state.batch.stop = true;
    },
    runSuccess(state, action: PayloadAction<LabRun>) {
      state.batch.done++;
      state.batch.results.push(action.payload);
    },
    batchSuccess(state) {
      state.batch.running = false;
    },
    batchReject(state) {
      state.batch.running = false;
      state.labError = true;
    },
    reset(state) {
      return { ...initialState, revision: state.revision + 1, batch: { ...initialState.batch, stop: true } };
    },
    loadJournal(state, action: PayloadAction<Filters>) {
      state.revision++;
      state.items = [];
      state.total = 0;
      state.filters = action.payload;
      state.loading = true;
      state.failed = false;
    },
    loadJournalSuccess(state, action: PayloadAction<{ items: JournalEntry[]; total: number; revision: number }>) {
      if (state.revision !== action.payload.revision) return;
      Object.assign(state, action.payload);
      state.loading = false;
    },
    loadJournalReject(state, action: PayloadAction<number>) {
      if (state.revision !== action.payload) return;
      state.loading = false;
      state.failed = true;
      state.items = [];
      state.total = 0;
    },
    loadDetail(state, action: PayloadAction<number>) {
      state.selectedId = action.payload;
      state.detail = undefined;
      state.detailLoading = true;
      state.detailFailed = false;
    },
    loadDetailSuccess(state, action: PayloadAction<JournalDetail>) {
      if (state.selectedId !== action.payload.entry.id) return;
      state.detail = action.payload;
      state.detailLoading = false;
    },
    loadDetailReject(state, action: PayloadAction<number>) {
      if (state.selectedId !== action.payload) return;
      state.detailLoading = false;
      state.detailFailed = true;
    },
    closeDetail(state) {
      state.selectedId = undefined;
      state.detail = undefined;
      state.detailLoading = false;
    },
  },
});
